import { escapePanelAttribute } from "@productlane/lib/src/tiptap/panel-markdown"

import type { MdxBlockReader } from "./registry"

// The first branch swallows a longer fence holding a ```mermaid sample, so
// matchAll (no overlaps) never reads that sample as a diagram.
const MERMAID_FENCE =
	/^(`{4,})([^\n]*)\n(?=(?:(?!^\1)[\s\S])*?^```mermaid)([\s\S]*?)^\1`*[ \t]*$|^```mermaid\b([^\n]*)\n([\s\S]*?)^```[ \t]*$/gm

const PLACEMENTS = new Set([
	"top-left",
	"top-right",
	"bottom-left",
	"bottom-right",
])

const escapeText = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

export function readMermaidProps(info: string): {
	placement: string | null
	actions: string | null
} {
	const placement = /placement=["']([^"']+)["']/.exec(info)?.[1] ?? null
	const actions = /actions=\{?\s*(true|false)\s*\}?/.exec(info)?.[1] ?? null
	return {
		placement: placement && PLACEMENTS.has(placement) ? placement : null,
		actions,
	}
}

export function mermaidHtml(
	source: string,
	props: { placement: string | null; actions: string | null },
): string {
	const attributes = [
		props.placement ? ` data-placement="${props.placement}"` : "",
		props.actions ? ` data-actions="${props.actions}"` : "",
	].join("")
	return `<div data-type="mermaid"${attributes}><pre data-mermaid-source>${escapeText(
		source.replace(/\n+$/, ""),
	)}</pre></div>`
}

// Same shape the default fence renderer gives a code block.
function codeFenceHtml(info: string, code: string): string {
	const [language, ...rest] = info.trim().split(/\s+/)
	const title = rest.join(" ")
	const classAttribute = language
		? ` class="language-${escapePanelAttribute(language)}"`
		: ""
	return `<pre${language ? ` data-language="${escapePanelAttribute(language)}"` : ""}${
		title ? ` data-title="${escapePanelAttribute(title)}"` : ""
	}${classAttribute}><code${classAttribute}>${escapeText(code.replace(/\n$/, ""))}</code></pre>`
}

export const mermaidReaders: MdxBlockReader[] = [
	{
		name: "mermaid",
		pattern: MERMAID_FENCE,
		render: (match) => {
			if (match[1]) return codeFenceHtml(match[2] ?? "", match[3] ?? "")
			return mermaidHtml(match[5] ?? "", readMermaidProps(match[4] ?? ""))
		},
	},
]
